import { motion } from "framer-motion";
import { Mail, MessageCircle, Table2, AlertTriangle, ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { fadeUp, stagger, viewport } from "@/lib/motion";

const ICONS = { Emails: Mail, WhatsApp: MessageCircle, Excel: Table2, "Missed Issues": AlertTriangle };
const FLOW = [
  { label: "Emails", sub: "Threads buried" },
  { label: "WhatsApp", sub: "Scattered chats" },
  { label: "Excel", sub: "Manual tracking" },
  { label: "Missed Issues", sub: "Costly delays", danger: true },
];
const FRAGMENTS = ["Shipment delays", "Missing documents", "Inspection issues", "Production delays", "Unexpected ETA changes", "Supplier follow-ups", "Manual issue tracking"];

export const Problem = () => {
  return (
    <section id="problem" className="relative py-24 md:py-32 bg-white" data-testid="section-problem">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl">
          <Reveal><span className="text-xs font-semibold uppercase tracking-[0.15em] text-brand">The Problem</span></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 font-heading text-3xl md:text-5xl font-bold text-navy tracking-tight leading-tight text-balance">
              Critical issues shouldn&apos;t hide inside emails.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-lg text-ink-500 leading-relaxed">
              Operations live across a dozen disconnected tools. When a document goes missing or an ETA
              slips, the signal is buried — and by the time someone notices, it&apos;s already costly.
            </p>
          </Reveal>
        </div>

        <motion.ul variants={stagger(0.06)} initial="hidden" whileInView="show" viewport={viewport} className="mt-10 flex flex-wrap gap-2.5">
          {FRAGMENTS.map((f) => (
            <motion.li key={f} variants={fadeUp} className="px-4 py-2 rounded-full border border-slate-200 bg-canvas-soft text-sm text-ink-500">{f}</motion.li>
          ))}
        </motion.ul>

        <div className="mt-16 rounded-4xl border border-slate-100 bg-canvas-soft p-8 md:p-12">
          <motion.div variants={stagger(0.12)} initial="hidden" whileInView="show" viewport={viewport}
            className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
            {FLOW.map((node, i) => {
              const Icon = ICONS[node.label];
              return (
                <div key={node.label} className="flex flex-col md:flex-row items-center gap-4 md:flex-1">
                  <motion.div variants={fadeUp}
                    className={`group relative w-full md:w-auto md:flex-1 rounded-2xl bg-white border ${node.danger ? "border-priority-border shadow-[0_8px_30px_-8px_rgba(217,119,6,0.15)]" : "border-slate-100 shadow-soft"} px-6 py-5 flex items-center gap-4`}>
                    <span className={`grid place-items-center h-11 w-11 rounded-xl ${node.danger ? "bg-priority-bg text-priority-text" : "bg-brand-soft text-brand"}`}>
                      <Icon className="h-5 w-5" strokeWidth={1.75} />
                    </span>
                    <div className="leading-tight">
                      <p className="font-semibold text-navy">{node.label}</p>
                      <p className="text-xs text-ink-400 mt-0.5">{node.sub}</p>
                    </div>
                  </motion.div>
                  {i < FLOW.length - 1 && (
                    <motion.span variants={fadeUp} className="text-ink-300 rotate-90 md:rotate-0 shrink-0">
                      <ArrowRight className="h-5 w-5" strokeWidth={1.75} />
                    </motion.span>
                  )}
                </div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
